import {
  getCreationProcessDisplayedName,
  getInternalMaterialProductComponentLabel,
  MaterialProductInfoListItem,
  MaterialProductValueType,
} from 'api';
import { getCountryNameByCountryAbbr } from 'form';
import { translate } from 'i18n';
import { capitalizeString, DataGridColumn } from 'ui';

const NOT_SPECIFIED = translate('backlog.materialProductsList.values.notSpecified', { namespace: 'features' });

export const columns: DataGridColumn<MaterialProductInfoListItem>[] = [
  {
    columnSize: 'small',
    field: 'id',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.id', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      return row?.id;
    },
  },
  {
    columnSize: 'medium',
    field: 'country',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.country', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      return getCountryNameByCountryAbbr(row?.country);
    },
  },
  {
    columnSize: 'xLarge',
    field: 'material_spec',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.materialSpec', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      return row?.material_spec.title;
    },
  },
  {
    columnSize: 'small',
    field: 'material_grade',
    filterable: true,
    headerName: translate('backlog.materialProductsList.columns.materialGrade', { namespace: 'features' }),
    sortable: false,
    type: 'string',
    valueGetter: (_value, row) => {
      return row?.material_grade.name;
    },
  },
  {
    columnSize: 'medium',
    field: 'material_origin',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.materialOrigin', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      return capitalizeString(row?.material_origin?.type || '') || NOT_SPECIFIED;
    },
  },
  {
    columnSize: 'small',
    field: 'creation_process',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.process', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      if (row?.creation_process) {
        return getCreationProcessDisplayedName(row?.creation_process);
      }
      return NOT_SPECIFIED;
    },
  },
  {
    columnSize: 'small',
    field: 'is_food_grade',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.foodGrade', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      if (row?.is_food_grade !== null) {
        return row?.is_food_grade
          ? translate('backlog.materialProductsList.values.yes', { namespace: 'features' })
          : translate('backlog.materialProductsList.values.no', { namespace: 'features' });
      }
      return NOT_SPECIFIED;
    },
  },
  {
    columnSize: 'small',
    field: 'has_caps_and_labels',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.capsAndLabels', { namespace: 'features' }),
    sortable: false,
    valueGetter: (_value, row) => {
      if (row?.has_caps_and_labels !== null) {
        return row?.has_caps_and_labels
          ? translate('backlog.materialProductsList.values.yes', { namespace: 'features' })
          : translate('backlog.materialProductsList.values.no', { namespace: 'features' });
      }
      return NOT_SPECIFIED;
    },
  },
  {
    columnSize: 'large',
    field: 'seller_material_component_values',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.sellerMaterialComponents', {
      namespace: 'features',
    }),
    sortable: false,
    valueGetter: (_value, row) => {
      if (row?.material_component_values) {
        return row.material_component_values
          .filter(
            (materialComponentValue: NonNullable<MaterialProductInfoListItem['material_component_values']>[number]) =>
              materialComponentValue.type === MaterialProductValueType.SELLER
          )
          .map(
            (materialComponentValue: NonNullable<MaterialProductInfoListItem['material_component_values']>[number]) =>
              getInternalMaterialProductComponentLabel(materialComponentValue)
          )
          .join(', ');
      }
      return NOT_SPECIFIED;
    },
  },
  {
    columnSize: 'large',
    field: 'buyer_material_component_values',
    filterable: false,
    headerName: translate('backlog.materialProductsList.columns.buyerMaterialComponents', {
      namespace: 'features',
    }),
    sortable: false,
    valueGetter: (_value, row) => {
      if (row?.material_component_values) {
        return row.material_component_values
          .filter(
            (materialComponentValue: NonNullable<MaterialProductInfoListItem['material_component_values']>[number]) =>
              materialComponentValue.type === MaterialProductValueType.BUYER
          )
          .map(
            (materialComponentValue: NonNullable<MaterialProductInfoListItem['material_component_values']>[number]) =>
              getInternalMaterialProductComponentLabel(materialComponentValue)
          )
          .join(', ');
      }
      return NOT_SPECIFIED;
    },
  },
];
